/** Host matching helpers for the SDK's `CategoryPageSubheaders` registry. */

/**
 * Resolves the current category the same way `useProductListInjections`
 * does for `CategoryPageInterstitials`/`CategoryPageSlots`: prefer the
 * `category` route param, falling back to `slug`.
 */
export const getCategoryToMatch = (query: Record<string, unknown> | undefined): string | undefined => {
  const category = query?.category ?? query?.slug;
  return typeof category === 'string' ? category : undefined;
};

/**
 * Fails closed to `undefined` (today's H1-only layout) unless there's an
 * entry with an exact category match and a valid, non-empty, non-whitespace
 * `text`. Deliberately does not fall back to an entry with no `category`
 * (no wildcard/default entry in this version).
 */
export const findMatchingCategorySubheaderText = (
  entries: unknown,
  categoryToMatch: string | undefined
): string | undefined => {
  if (!Array.isArray(entries) || entries.length === 0 || !categoryToMatch) {
    return undefined;
  }

  // Registry values cross a remote-module runtime boundary, so validate the
  // container and each matching entry instead of trusting compile-time types.
  // Duplicate categories intentionally use the first matching entry,
  // consistent with Array.find and the SDK's ordered registry conventions.
  const match: unknown = entries.find(
    (entry: unknown) =>
      entry !== null && typeof entry === 'object' && 'category' in entry && entry.category === categoryToMatch
  );

  if (match === null || typeof match !== 'object' || !('text' in match) || typeof match.text !== 'string') {
    return undefined;
  }

  const trimmed = match.text.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};
